/**
 * Suggested-tab container.
 *
 * Owns the SE-5523 recommendation read and the session-scoped dismissal, and
 * nothing else — Add is passed IN from the panel so a single re-entry guard
 * covers both the Suggested rows and the search rows. Two guards would be two
 * chances to double-add the same player from two lists.
 *
 * SE-6554 moved the reason-filter and GPR-sort SELECTIONS up to the panel for
 * the same reason: the tab strip above this body renders the filtered count, so
 * a selection held down here could never reach it. This container now applies
 * what it is handed and reports nothing back up.
 * @module features/player-kanban/components/organisms/SuggestedPlayersList
 */

import React, { useCallback } from "react";

import { AnalyticsTracker } from "@/features/analytics";
import type { SuggestionReasonCategory } from "@/generated/graphql";

import useSuggestedPlayers from "../../../hooks/useSuggestedPlayers";

import SuggestedPlayersListView from "./SuggestedPlayersListView";

/** Props for the SuggestedPlayersList container. */
interface SuggestedPlayersListContainerProps {
  /** The open pipeline. */
  readonly kanbanBoardId: string | null | undefined;
  /** Player ids with an add in flight, owned by the panel. */
  readonly addingPlayerIds: readonly string[];
  /**
   * Add a player to this pipeline. Takes the candidate's reason categories so
   * the panel can attribute the SE-5508 `suggestion_acted` event; the guard
   * against a double-add still lives in `useAddSuggestedPlayer`.
   */
  readonly onAdd: (
    playerId: string,
    reasons?: readonly SuggestionReasonCategory[]
  ) => void;
}

/**
 * Render the Suggested tab.
 * @param root0 - Component props.
 * @param root0.kanbanBoardId - The open pipeline.
 * @param root0.addingPlayerIds - Ids with an add in flight.
 * @param root0.onAdd - Add callback.
 */
const SuggestedPlayersListContainer = ({
  kanbanBoardId,
  addingPlayerIds,
  onAdd,
}: SuggestedPlayersListContainerProps) => {
  // The SAME query, with the same variables, that the panel reads for the tab
  // count. Apollo deduplicates the request and both subscribers observe one
  // normalized cache entry, so the badge and this list are always the same set.
  const { candidates, isLoading, error, dismiss } =
    useSuggestedPlayers(kanbanBoardId);

  /*
   * Candidates render in their incoming `bestRank` order (strongest first) —
   * the PC frames carry no sort control, so the SE-6554 GPR sort is gone and
   * the recommendation ranking is the one order the tab shows.
   */
  const visibleCandidates = candidates;

  /**
   * SE-5508: attach the candidate's reason categories to the add. This layer
   * is the only one holding the candidate, so the lookup happens here rather
   * than being threaded through the row. The emission itself stays in
   * `useAddSuggestedPlayer`, behind its in-flight guard, so a double-clicked
   * Add cannot emit twice.
   */
  const handleAdd = useCallback(
    (playerId: string) => {
      const candidate = candidates.find(entry => entry.player.id === playerId);
      onAdd(playerId, candidate?.reasons);
    },
    [candidates, onAdd]
  );

  /**
   * SE-5508 `suggestion_acted`, dismiss variant.
   *
   * Emitted here rather than inside `dismissedSuggestionsStore` because the
   * store holds ids only, not the candidate's `reasons`. Exactly-once comes
   * from the `candidates` lookup: `useSuggestedPlayers` filters dismissed
   * entries out of that list, so a second dismiss of the same suggestion finds
   * nothing and emits nothing — matching the store's own idempotent early
   * return. Dismiss fires NO network request, so there is no success signal to
   * wait for and nothing that could fail.
   */
  const handleDismiss = useCallback(
    (suggestionId: string) => {
      const candidate = candidates.find(entry => entry.id === suggestionId);
      if (!candidate) return;
      void AnalyticsTracker.pipeline.logSuggestionActed({
        action: "dismiss",
        reason_categories: candidate.reasons.join(","),
        board_id: kanbanBoardId ?? "",
      });
      dismiss(suggestionId);
    },
    [candidates, dismiss, kanbanBoardId]
  );

  return (
    <SuggestedPlayersListView
      candidates={visibleCandidates}
      isLoading={isLoading}
      error={error}
      addingPlayerIds={addingPlayerIds}
      onAdd={handleAdd}
      onDismiss={handleDismiss}
    />
  );
};

export default SuggestedPlayersListContainer;
